import { Timestamp } from "firebase/firestore";

export class Mail {
    private name: string|undefined;
    private id: string;
    private delivery: Delivery;
    private template: MailTemplate;
    private message: MailMessage;
    private to: Array<string>;

    constructor(name:string|undefined, id: string, delivery: Delivery, template: MailTemplate, message: MailMessage, to: Array<string>) {
        this.setName(name);
        this.setId(id);
        this.setDelivery(delivery);
        this.setTemplate(template);
        this.setMessage(message);
        this.setTo(to);
    }

    static fromJson(json:any, id:string) {
        const templateData = json.template;
        var template;
        if (templateData !== null && templateData !== undefined) {
            template = MailTemplate.fromJson(templateData);
        }

        const messageData = json.message;
        var message;
        if (messageData !== null && messageData !== undefined) {
            message = MailMessage.fromJson(messageData);
        }

        const mail = new Mail(undefined, id, Delivery.fromJson(json.delivery), template, message, json.to);
        return mail;
    }

    static fromLocal(json:any, name:string) {
        console.log(`name is ${name}`);
        const templateData = json.template;
        var template;
        if (templateData !== null && templateData !== undefined) {
            template = MailTemplate.fromJson(templateData);
        }

        const messageData = json.message;
        var message;
        if (messageData !== null && messageData !== undefined) {
            message = MailMessage.fromJson(messageData);
        }

        const mail = new Mail(name, json.id, Delivery.fromLocal(json.delivery), template, message, json.to);
        return mail;
    }

    toJson(): any {
        return {
            id: this.id,
            to: this.to,
            delivery: this.delivery.toJson(),
            template: this.template !== undefined && this.template !== null ? this.template.toJson() : undefined,
            message: this.message !== undefined && this.message !== null ? this.message.toJson() : undefined,
        }
    }

    setName(newName:string|undefined) {
        this.name = newName;
    }
    setId(newId:string) {
        this.id = newId;
    }
    setDelivery(newDelivery:Delivery) {
        this.delivery = newDelivery;
    }
    setTemplate(newTemplate:MailTemplate) {
        this.template = newTemplate;
    }
    setMessage(newMessage:MailMessage) {
        this.message = newMessage;
    }
    setTo(newTo:Array<string>) {
        this.to = newTo;
    }

    getName() {
        return this.name;
    }
    getId() {
        return this.id;
    }
    getDelivery() {
        return this.delivery;
    }
    getTemplate() {
        return this.template;
    }
    getMessage() {
        return this.message;
    }
    getTo() {
        return this.to;
    }
}

export class Delivery {
    private attemps: number;
    private endTime: Timestamp;
    private error: any;
    private info: DeliveryInfo;
    private startTime: Timestamp;
    private state: string;

    constructor(attemps: number, endTime: Timestamp, error: any, info: DeliveryInfo, startTime: Timestamp, state: string) {
        this.setAttemps(attemps);
        this.setEndTime(endTime);
        this.setError(error);
        this.setInfo(info);
        this.setStartTime(startTime);
        this.setState(state);
    }

    static fromJson(json:any) {
        const delivery = new Delivery(json.attempts, json.endTime, json.error, DeliveryInfo.fromJson(json.info), json.startTime, json.state);
        return delivery;
    }

    static fromLocal(json:any) {
        const delivery = new Delivery(json.attempts, new Timestamp(json.endTime.seconds, json.endTime.nanoseconds), json.error, DeliveryInfo.fromJson(json.info), new Timestamp(json.startTime.seconds, json.startTime.nanoseconds), json.state);
        return delivery;
    }

    toJson() {
        return {
            attempts: this.attemps,
            endTime: this.endTime,
            error: this.error,
            startTime: this.startTime,
            state: this.state,
            info: this.info.toJson(),
        }
    }

    setAttemps(newAttemps:number) {
        if (newAttemps >= 0) {
            this.attemps = newAttemps;
        }
    }
    setEndTime(newEndTime:Timestamp) {
        this.endTime = newEndTime;
    }
    setError(newError:any) {
        this.error = newError;
    }
    setInfo(newInfo:DeliveryInfo) {
        this.info = newInfo;
    }
    setStartTime(newStartTime:Timestamp) {
        this.startTime = newStartTime;
    }
    setState(newState:string) {
        this.state = newState;
    }

    getAttemps() {
        return this.attemps;
    }
    getEndTime() {
        return this.endTime;
    }
    getError() {
        return this.error;
    }
    getInfo() {
        return this.info;
    }
    getStartTime() {
        return this.startTime;
    }
    getState() {
        return this.state;
    }
}

export class DeliveryInfo {
    private accepted: Array<string>;
    private messageId: string;
    private pending: Array<string>;
    private rejected: Array<string>;
    private response: string;

    constructor(accepted: Array<string>, messageId: string, pending: Array<string>, rejected: Array<string>, response: string) {
        this.setAccepted(accepted);
        this.setMessageId(messageId);
        this.setPending(pending);
        this.setRejected(rejected);
        this.setResponse(response);
    }

    static fromJson(json:any) {
        const deliveryInfo = new DeliveryInfo(json.accepted, json.messageId, json.pending, json.rejected, json.response);
        return deliveryInfo;
    }

    toJson() {
        return {
            accepted: this.accepted,
            messageId: this.messageId,
            pending: this.pending,
            rejected: this.rejected,
            response: this.response,
        }
    }

    setAccepted(newAccepted:Array<string>) {
        this.accepted = newAccepted;
    }

    setMessageId(newMessageId:string) {
        this.messageId = newMessageId;
    }

    setPending(newPending:Array<string>) {
        this.pending = newPending;
    }
    
    setRejected(newRejected:Array<string>) {
        this.rejected = newRejected;
    }

    setResponse(newResponse:string) {
        this.response = newResponse;
    }

    getAccepted() {
        return this.accepted;
    }

    getMessageId() {
        return this.messageId;
    }

    getPending() {
        return this.pending;
    }

    getRejected() {
        return this.rejected;
    }

    getResponse() {
        return this.response;
    }
}

export class MailTemplate {
    private data: Map<string, any>;
    private name: string;

    constructor(data:Map<string, any>, name:string) {
        this.setData(data);
        this.setName(name);
    }

    static fromJson(json:any) {
        const mapData = new Map();
        
        const actualData = json.data;
        const keys = Object.keys(actualData);
        for (var key in Object.keys(actualData)) {
            mapData.set(keys[key], actualData[keys[key]]);
        }

        const mailTemplate = new MailTemplate(mapData, json.name);
        return mailTemplate;
    }

    toJson() {
        const jsonData = {};

        this.data.forEach((value, key) => {
            jsonData[key] = value;
        });

        return {
            name: this.name,
            data: jsonData,
        }
    }

    setData(newData:Map<string, any>) {
        this.data = newData;
    }

    setName(newName:string) { 
        this.name = newName;
    }

    getData() {
        return this.data;
    }

    getName() {
        return this.name;
    }
}

export class MailMessage {
    private subject: string;
    private text: string;
    private html: string;

    constructor(subject:string, text:string, html:string) {
        this.setSubject(subject);
        this.setText(text);
        this.setHtml(html);
    }

    static fromJson(json:any) {
        const mailMessage = new MailMessage(json.subject, json.text, json.html);
        return mailMessage;
    }

    toJson() {
        return {
            subject: this.subject,
            text: this.text,
            html: this.html,
        }
    }

    setSubject(newSubject:string) {
        this.subject = newSubject;
    }

    setText(newText:string) {
        this.text = newText;
    }

    setHtml(newHtml:string) {
        this.html = newHtml;
    }

    getSubject() {
        return this.subject;
    }

    getText() {
        return this.text;
    }

    getHtml() {
        return this.html;
    }
}

export class MailTemplateData {
    private html: string;
    private subject: string;

    constructor(html:string, subject:string) {
        this.setHtml(html);
        this.setSubject(subject);
    }

    static fromJson(json:any) {
        const mailTemplateData = new MailTemplateData(json.html, json.subject);
        return mailTemplateData;
    }

    setHtml(newHtml:string) {
        this.html = newHtml;
    }

    setSubject(newSubject:string) {
        this.subject = newSubject;
    }

    getHtml() {
        return this.html;
    }

    getSubject() {
        return this.subject;
    }
}